import React, { Dispatch, SetStateAction, useLayoutEffect, useRef, useState } from "react";
import cn from "classnames";
import { useTranslation } from "shared/localization/localization";
import { CustomScrollBar } from "shared/ui/CustomScrollBar";
import styles from "./TagSuggestion.module.scss";

type TagSuggestionProps = {
  tagsSuggestion: Array<string>;
  inputRef: React.MutableRefObject<HTMLInputElement>;
  addTag: (tag: string) => void;
  setSearchTag: Dispatch<SetStateAction<string>>;
};

export const TagSuggestion: RFC<TagSuggestionProps> = ({ tagsSuggestion, inputRef, addTag, setSearchTag }) => {
  const { t } = useTranslation();
  const [indexSuggestion, setIndexSuggestion] = useState(0);

  const onKeyDown = (event: KeyboardEvent) => {
    const arrowUpBtn = "ArrowUp";
    const arrowRightBtn = "ArrowRight";
    const arrowDownBtn = "ArrowDown";
    const enterBtn = "Enter";
    const input = event.currentTarget as HTMLInputElement;
    if (event.key === arrowUpBtn) {
      event.preventDefault();
      if (indexSuggestion === 0) return;
      setIndexSuggestion(indexSuggestion - 1);
    }
    if (event.key === arrowRightBtn) {
      if (tagsSuggestion.length === 0) return;

      input.value = tagsSuggestion[indexSuggestion];
      setSearchTag(tagsSuggestion[indexSuggestion]);
      setIndexSuggestion(0);
    }
    if (event.key === arrowDownBtn) {
      event.preventDefault();
      if (indexSuggestion >= tagsSuggestion.length - 1) return;
      setIndexSuggestion(indexSuggestion + 1);
    }
    if (event.key === enterBtn) {
      event.preventDefault();
      addTag(tagsSuggestion[indexSuggestion]);
      input.value = "";
      setSearchTag("");
      setIndexSuggestion(0);
    }
  };

  useLayoutEffect(() => {
    const lastIndex = Math.max(tagsSuggestion.length - 1, 0);
    setIndexSuggestion((index) => Math.min(Math.max(index, 0), lastIndex));
  }, [tagsSuggestion.length]);

  const onKeyDownRef = useRef(onKeyDown);
  useLayoutEffect(() => {
    onKeyDownRef.current = onKeyDown;
  });

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleKeyDown = (event: KeyboardEvent) => onKeyDownRef.current(event);
    input.addEventListener("keydown", handleKeyDown);

    return () => {
      input.removeEventListener("keydown", handleKeyDown);
    };
  }, [inputRef]);

  if (!tagsSuggestion.length) return null;

  return (
    <div
      className={styles.tagListContainer}
      onMouseDown={(event) => {
        event.preventDefault();
      }}
    >
      <CustomScrollBar>
        <ul className={styles.tagList}>
          {tagsSuggestion.map((tag, index) => {
            return (
              <li
                key={tag}
                className={cn(styles.tag, { [styles.active]: indexSuggestion === index })}
                onMouseEnter={() => {
                  setIndexSuggestion(index);
                }}
                onClick={() => {
                  addTag(tag);
                }}
              >
                {tag} <span className={styles.addBtn}>{t("Add")}</span>
              </li>
            );
          })}
        </ul>
      </CustomScrollBar>
    </div>
  );
};
